import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'

import type { EngagementType } from '../../graphqlEnums'
import { MentionData_Mention } from '../../Mentions/fragments/mentionData'

/**
 * Generic core over the unified `engagementConversationById` query. Fetches a
 * single engagement's conversation: the thread `post` plus its `ancestors`
 * connection. Kind-agnostic — pass `engagementType` to fetch a `mention`
 * (and, once the BE supports it, a `comment`) conversation.
 *
 * Domain hooks wrap this and narrow / reshape the polymorphic `Engagement`
 * items to their concrete type: `useMentionConversation` today, and a
 * `useCommentConversation` once comments migrate off the legacy
 * `commentThread` query — same wrapper pattern, swap the `engagementType`.
 *
 * Declarative: callers pass `enabled` and the query fires on expand, so a
 * single fetch powers both the card's post miniature (`post`) and its context
 * section (`ancestors`).
 *
 * The selection set inlines `... on Mention` for the populated branch and
 * aliases the `Post` branch's conflicting `id` / `text` (`PostId!` / `String!`
 * vs `EngagementId!` / `String`) so codegen can merge the union. Only the
 * `Mention` branch is populated today — posts live in the same collection as
 * mentions, surfaced as a `mentionType: post` item.
 */
export const EngagementConversationByIdQueryGQL = graphql(
  /* GraphQL */ `
    query EngagementConversationById($input: EngagementConversationByIdInput!) {
      engagementConversationById(input: $input) {
        post {
          __typename
          ... on Mention {
            ...MentionData_Mention
            id
            parentId
            channelId
            organizationId
            serviceType
            mentionType
            status
            text
            publicUrl
            publishedAt
            createdAt
            isOwnEngagement
            contentType
            author {
              name
              username
              avatar
              letterAvatar {
                letter
                textColor
                backgroundColor
              }
            }
            assets {
              __typename
              id
              mimeType
              thumbnail
              source
              type
              ... on ImageAsset {
                image {
                  altText
                  width
                  height
                  isAnimated
                }
              }
              ... on VideoAsset {
                video {
                  width
                  height
                }
              }
            }
          }
          ... on Post {
            postId: id
            postText: text
          }
        }
        ancestors(first: 20) {
          edges {
            node {
              __typename
              ... on Mention {
                ...MentionData_Mention
                id
                parentId
                channelId
                organizationId
                serviceType
                mentionType
                status
                text
                publicUrl
                publishedAt
                createdAt
                isOwnEngagement
                contentType
                suggestions {
                  text
                  source
                }
                author {
                  name
                  username
                  avatar
                  letterAvatar {
                    letter
                    textColor
                    backgroundColor
                  }
                }
                assets {
                  __typename
                  id
                  mimeType
                  thumbnail
                  source
                  type
                  ... on ImageAsset {
                    image {
                      altText
                      width
                      height
                      isAnimated
                    }
                  }
                  ... on VideoAsset {
                    video {
                      width
                      height
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `,
  [MentionData_Mention],
)

/** A polymorphic `Engagement` node — callers narrow it by `__typename`. */
type EngagementConversationData = NonNullable<
  ResultOf<
    typeof EngagementConversationByIdQueryGQL
  >['engagementConversationById']
>
type EngagementNode =
  | NonNullable<EngagementConversationData['post']>
  | NonNullable<
      EngagementConversationData['ancestors']['edges'][number]['node']
    >

export type EngagementConversation = {
  /** Thread root (`Post` / `Mention (mentionType: post)`). Null when not found. */
  post: EngagementNode | null
  /** Context items preceding the engagement, newest first. */
  ancestors: EngagementNode[]
}

type EngagementConversationByIdArgs = {
  engagementId: string
  engagementType: EngagementType
  /** Skip the query until the card is expanded. Defaults to `true`. */
  enabled?: boolean
}

export type UseEngagementConversationByIdResult = EngagementConversation & {
  loading: boolean
  error: ErrorLike | null
}

export const useEngagementConversationById = ({
  engagementId,
  engagementType,
  enabled = true,
}: EngagementConversationByIdArgs): UseEngagementConversationByIdResult => {
  const { data, loading, error } = useQuery<
    ResultOf<typeof EngagementConversationByIdQueryGQL>,
    VariablesOf<typeof EngagementConversationByIdQueryGQL>
  >(EngagementConversationByIdQueryGQL, {
    skip: !enabled,
    variables: { input: { engagementId, engagementType } },
  })

  const conversation = data?.engagementConversationById

  return {
    post: conversation?.post ?? null,
    ancestors: conversation?.ancestors.edges.map((edge) => edge.node) ?? [],
    loading,
    error: error ?? null,
  }
}
